import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Role } from '../../types';
import { ProtectedAction } from '../ProtectedAction';

interface RoleFormProps {
  role?: Role;
  onSubmit: (role: Omit<Role, 'id' | 'createdAt'>) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  /** Error devuelto por la API (crear/editar rol); se muestra dentro del formulario. */
  serverError?: string | null;
  /** Llamar al cambiar el formulario para ocultar el error del servidor. */
  onDismissServerError?: () => void;
}

/**
 * Secciones: Ver, Agregar (crear), Editar (actualizar), Eliminar. El permiso legacy `manage_*` sigue existiendo en BD por compatibilidad.
 */
const permissionSections: { title: string; permissions: string[]; hint?: string }[] = [
  { title: 'Dashboard', permissions: ['view_dashboard'], hint: 'Panel principal' },
  {
    title: 'Propiedades',
    permissions: [
      'view_properties',
      'create_properties',
      'update_properties',
      'delete_properties',
    ],
    hint: 'Ver · Agregar = nueva propiedad · Editar = cambios y documentos · Eliminar = botón borrar',
  },
  {
    title: 'Unidades',
    permissions: ['view_units', 'create_units', 'update_units', 'delete_units'],
    hint: 'Ver · Agregar = nueva unidad · Editar = datos de unidad · Eliminar = quitar unidad',
  },
  {
    title: 'Inversionistas',
    permissions: ['view_investors', 'create_investors', 'update_investors', 'delete_investors'],
    hint: 'Ver · Agregar = alta e inversiones · Editar = datos e inversiones · Eliminar = borrar',
  },
  {
    title: 'Inquilinos',
    permissions: [
      'view_tenants',
      // Legacy (compatibilidad): algunos roles antiguos solo traen view_rentals.
      'view_rentals',
      // Legacy (compatibilidad): algunos roles antiguos usan manage_rentals.
      'manage_rentals',
      'create_rentals',
      'update_rentals',
      'delete_rentals',
      'renew_tenant_contract',
    ],
    hint:
      'Inquilinos y contratos. Ver = acceso al listado/detalle. Agregar/Editar/Eliminar controlan acciones dentro del módulo. Renovar = extender contrato.',
  },
  {
    title: 'Solicitudes',
    permissions: ['view_applications', 'create_applications', 'update_applications', 'delete_applications'],
    hint: 'Solicitudes y prospectos · Agregar / Editar / Eliminar por separado',
  },
  {
    title: 'Prospectos',
    permissions: ['view_prospects'],
    hint: 'Acceso al listado y detalle de prospectos (menú Rentas → Prospectos).',
  },
  {
    title: 'Pagos',
    permissions: ['view_rent_payments'],
    hint: 'Acceso a resumen de pagos y vista de pagos por inquilino (menú Rentas → Pagos).',
  },
  {
    title: 'Usuarios',
    permissions: ['view_users', 'create_users', 'update_users', 'delete_users'],
    hint: 'Ver · Agregar = crear usuario · Editar = modificar usuario · Eliminar = botón borrar',
  },
  {
    title: 'Estadísticas',
    permissions: [
      'view_statistics',
      'view_statistics_dashboard',
      'view_statistics_occupancy',
      'view_statistics_revenue',
      'view_statistics_price_m2',
      'view_statistics_units',
    ],
    hint:
      'Ver todas (legado) equivale a las cinco secciones. O elige solo Dashboard resumen, Ocupación, Ingresos brutos, Valor m² o Unidades.',
  },
  {
    title: 'Administración de inmuebles',
    permissions: [
      'view_property_management',
      'create_property_management',
      'update_property_management',
      'delete_property_management',
    ],
    hint: 'Mantenimientos de propiedades · Agregar / Editar / Eliminar por separado',
  },
  {
    title: 'Mantenimiento de inmuebles',
    permissions: [
      'view_maintenance_reports',
      'create_maintenance_reports',
      'update_maintenance_reports',
      'delete_maintenance_reports',
    ],
    hint: 'Reportes de mantenimiento · Agregar = nuevo reporte · Editar = estado, cotización, comentarios · Eliminar = borrar reporte',
  },
  {
    title: 'Configuración',
    permissions: ['manage_settings', 'create_roles', 'update_roles', 'delete_roles'],
    hint: 'Ajustes del sistema (manage_settings) · Roles: crear, editar permisos del rol, eliminar rol',
  },
  { title: 'Fideicomiso', permissions: ['view_fideicomiso'], hint: 'Sección KYC (Know Your Customer)' },
  {
    title: 'Otros',
    permissions: ['view_investor_account', 'view_prices'],
    hint: 'Cuenta inversionista · Ver montos y precios',
  },
  {
    title: 'Portal Inquilino',
    permissions: [
      'view_tenant_portal',
      'view_tenant_payments',
      'create_tenant_maintenance_report',
      'request_tenant_renewal',
    ],
    hint: 'Portal de acceso para inquilinos · Ver información, pagos, reportes y renovaciones',
  },
];

const allPermissionCodes = permissionSections.flatMap((s) => s.permissions);

const permissionLabels: Record<string, string> = {
  view_dashboard: 'Ver Dashboard',
  view_properties: 'Ver Propiedades',
  create_properties: 'Agregar propiedades',
  update_properties: 'Editar propiedades',
  delete_properties: 'Eliminar propiedades',
  view_units: 'Ver Unidades',
  create_units: 'Agregar unidades',
  update_units: 'Editar unidades',
  delete_units: 'Eliminar unidades',
  view_investors: 'Ver Inversionistas',
  create_investors: 'Agregar inversionistas',
  update_investors: 'Editar inversionistas',
  delete_investors: 'Eliminar inversionistas',
  view_rentals: 'Ver Rentas',
  view_prospects: 'Rentas: Prospectos',
  view_tenants: 'Rentas: Inquilinos',
  view_rent_payments: 'Rentas: Pagos',
  create_rentals: 'Agregar inquilinos / rentas',
  update_rentals: 'Editar inquilinos / rentas',
  delete_rentals: 'Eliminar inquilinos / rentas',
  manage_rentals: 'Gestionar rentas (legacy)',
  renew_tenant_contract: 'Renovar contratos de inquilinos',
  view_applications: 'Ver Solicitudes',
  create_applications: 'Agregar solicitudes / prospectos',
  update_applications: 'Editar solicitudes / prospectos',
  delete_applications: 'Eliminar solicitudes y prospectos',
  view_users: 'Ver Usuarios',
  create_users: 'Agregar usuarios',
  update_users: 'Editar usuarios',
  delete_users: 'Eliminar usuarios',
  view_statistics: 'Ver todas las estadísticas (legado)',
  view_statistics_dashboard: 'Estadísticas: Dashboard resumen',
  view_statistics_occupancy: 'Estadísticas: Ocupación',
  view_statistics_revenue: 'Estadísticas: Ingresos brutos',
  view_statistics_price_m2: 'Estadísticas: Valor m²',
  view_statistics_units: 'Estadísticas: Unidades',
  manage_settings: 'Gestionar Configuración',
  create_roles: 'Crear roles',
  update_roles: 'Editar roles',
  delete_roles: 'Eliminar roles',
  view_property_management: 'Ver Administración de inmuebles',
  create_property_management: 'Agregar administración de inmuebles',
  update_property_management: 'Editar administración de inmuebles',
  delete_property_management: 'Eliminar administración de inmuebles',
  view_maintenance_reports: 'Ver Reportes de mantenimiento',
  create_maintenance_reports: 'Agregar reportes de mantenimiento',
  update_maintenance_reports: 'Editar reportes de mantenimiento',
  delete_maintenance_reports: 'Eliminar reportes de mantenimiento',
  view_fideicomiso: 'Ver Fideicomiso',
  view_investor_account: 'Cuenta de Inversionista',
  view_prices: 'Ver precios',
  view_tenant_portal: 'Ver Portal Inquilino',
  view_tenant_payments: 'Ver Pagos Inquilino',
  create_tenant_maintenance_report: 'Crear Reporte Mantenimiento',
  request_tenant_renewal: 'Solicitar Renovación',
};

export const RoleForm: React.FC<RoleFormProps> = ({
  role,
  onSubmit,
  onCancel,
  isSubmitting = false,
  serverError = null,
  onDismissServerError,
}) => {
  const [formData, setFormData] = useState({
    name: role?.name || '',
    description: role?.description || '',
    permissions: role?.permissions || [] as string[],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name,
        description: role.description,
        permissions: role.permissions,
      });
    }
  }, [role]);

  const handleChange = (field: string, value: any) => {
    onDismissServerError?.();
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const togglePermission = (permission: string) => {
    onDismissServerError?.();
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const allPermissionsChecked = allPermissionCodes.length > 0 && allPermissionCodes.every((p) => formData.permissions.includes(p));
  const toggleAllPermissions = () => {
    onDismissServerError?.();
    setFormData((prev) => ({
      ...prev,
      permissions: allPermissionsChecked ? [] : [...allPermissionCodes],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre del rol es requerido';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'La descripción es requerida';
    }
    if (formData.permissions.length === 0) {
      newErrors.permissions = 'Debe seleccionar al menos un permiso';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit({
      name: formData.name,
      description: formData.description,
      permissions: formData.permissions,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Nombre */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nombre del Rol <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Ej: Administrador"
          />
          {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
        </div>

        {/* Descripción */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descripción <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 ${
              errors.description ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Ej: Acceso completo al sistema"
          />
          {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description}</p>}
        </div>
      </div>

      {/* Permisos por sección */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Permisos por sección <span className="text-red-500">*</span>
        </label>
        {errors.permissions && (
          <p className="mb-2 text-sm text-red-500">{errors.permissions}</p>
        )}
        <p className="mb-3 text-xs text-gray-500">
          <span className="font-medium">Agregar</span> y <span className="font-medium">Editar</span> son independientes: controlan botones de alta y de guardar cambios en cada módulo. <span className="font-medium">Eliminar</span> controla los botones de borrado.
        </p>
        <label className="flex items-center gap-2 mb-3 p-3 bg-primary/5 border border-primary/20 rounded-lg cursor-pointer hover:bg-primary/10 transition-colors">
          <div className="relative shrink-0">
            <input
              type="checkbox"
              checked={allPermissionsChecked}
              onChange={toggleAllPermissions}
              className="w-4 h-4 bg-white border-2 border-gray-500 rounded focus:ring-primary text-primary appearance-none checked:bg-primary checked:border-primary focus:outline-none focus:ring-2"
              style={{ colorScheme: 'light' }}
            />
            {allPermissionsChecked && (
              <svg
                className="absolute top-0 left-0 w-4 h-4 pointer-events-none"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="3"
                style={{ color: '#FA4999' }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <span className="text-sm font-medium text-gray-800">Dar todos los permisos</span>
        </label>
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200 max-h-[420px] overflow-y-auto">
          {permissionSections.map((section) => (
            <div key={section.title} className="border-b border-gray-200 last:border-0 pb-4 last:pb-0">
              <h4 className="text-sm font-semibold text-gray-800 mb-1">{section.title}</h4>
              {section.hint && (
                <p className="text-xs text-gray-500 mb-2">{section.hint}</p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {section.permissions.map((permission) => {
                  const isChecked = formData.permissions.includes(permission);
                  return (
                    <label
                      key={permission}
                      className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-white rounded transition-colors"
                    >
                      <div className="relative shrink-0">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => togglePermission(permission)}
                          className="w-4 h-4 bg-white border-2 border-gray-500 rounded focus:ring-primary text-primary appearance-none checked:bg-primary checked:border-primary focus:outline-none focus:ring-2"
                          style={{ colorScheme: 'light' }}
                        />
                        {isChecked && (
                          <svg
                            className="absolute top-0 left-0 w-4 h-4 pointer-events-none"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="3"
                            style={{ color: '#FA4999' }}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm text-gray-700">
                        {permissionLabels[permission] || permission.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-gray-500">
          {formData.permissions.length} permiso(s) seleccionado(s)
        </p>
      </div>

      {serverError && (
        <div
          className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm"
          role="alert"
        >
          {serverError}
        </div>
      )}

      {/* Botones */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancelar
        </button>
        <ProtectedAction
          permission={role ? ['update_roles', 'manage_settings'] : ['create_roles', 'manage_settings']}
          fallback={
            <button
              type="button"
              disabled
              title="No tienes permiso para realizar esta acción"
              className="px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 min-w-[140px] bg-gray-200 text-gray-500 cursor-not-allowed"
            >
              {role ? 'Guardar Cambios' : 'Crear Rol'}
            </button>
          }
        >
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 min-w-[140px] bg-accent text-white hover:bg-accent/90 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                {role ? 'Guardando…' : 'Creando…'}
              </>
            ) : (
              role ? 'Guardar Cambios' : 'Crear Rol'
            )}
          </button>
        </ProtectedAction>
      </div>
    </form>
  );
};

